import type { CharacterDto, AccountSummary, HouseChainDto } from '@rex-mortis/shared';

export interface AppState {
  /** The short-lived access token. Never persisted; lost on page reload (we
   *  recover by hitting /auth/refresh against the httpOnly cookie). */
  accessToken: string | null;
  account: AccountSummary | null;
  character: CharacterDto | null;
  /** Epoch-ms when `character` was last set from a server response. The
   *  resource bars tick forward from this anchor (current + regen × elapsed),
   *  so it MUST reflect when the server measured those values — otherwise a
   *  header rebuild would reset the prediction to a stale baseline and the bars
   *  would under-count regen until a manual page refresh. Stamped automatically
   *  by set() whenever the patch includes `character`. */
  characterAsOf: number | null;
  /** Bank interest schedule (from /api/me) for the sidebar wallet countdown. */
  bankInterest: { nextInterestAt: string; perDayCrowns: string; toWallet: boolean } | null;
  /** House bloodchain snapshot (from /api/me) for the sidebar chain tracker.
   *  Null when the player isn't in a House. */
  chain: HouseChainDto | null;
}

type Listener = (s: AppState) => void;

let state: AppState = {
  accessToken: null,
  account: null,
  character: null,
  characterAsOf: null,
  bankInterest: null,
  chain: null,
};

const listeners = new Set<Listener>();

export const store = {
  get(): AppState {
    return state;
  },
  set(patch: Partial<AppState>): void {
    const next = { ...state, ...patch };
    // Whenever a fresh character lands from the server, stamp the moment so the
    // resource bars can tick forward from the correct anchor. Caller may pass an
    // explicit characterAsOf to override (rarely needed).
    if ('character' in patch && !('characterAsOf' in patch)) {
      next.characterAsOf = Date.now();
    }
    state = next;
    for (const l of listeners) l(state);
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  clear(): void {
    state = { accessToken: null, account: null, character: null, characterAsOf: null, bankInterest: null, chain: null };
    for (const l of listeners) l(state);
  },
};
